import type {
  OfflineReadinessReport,
  OfflineReadinessState,
} from "./offline-window.ts";

/**
 * When a player should tell the server about its offline cache.
 *
 * The report is current state, not an event, so it needs to be fresh enough to
 * trust and rare enough to ignore. Steady state is 2 frames per hour per
 * device; the worst case is 12. Heartbeat, for comparison, is 144.
 *
 * Kept pure and separate from the transport so the cadence is testable without
 * a socket.
 */

/** Floor between reports whose only difference is the numbers. */
export const CACHE_REPORT_MIN_INTERVAL_MS = 5 * 60_000;
/** Upper bound on silence, so the console's "as of" stays meaningful. */
export const CACHE_REPORT_KEEPALIVE_MS = 30 * 60_000;

/**
 * Bytes are bucketed to whole MB so byte-level jitter (a re-download, a
 * different artwork encoding) cannot by itself trigger a send.
 */
export function cacheReportFingerprint(report: OfflineReadinessReport): string {
  return [
    report.state,
    report.cachedCount,
    report.neededCount,
    report.baseCachedCount,
    report.baseNeededCount,
    Math.round(report.totalBytes / 1_000_000),
    report.syncVersion ?? "",
  ].join("|");
}

export interface CacheReportMemo {
  fingerprint: string;
  state: OfflineReadinessState;
  /** When the last report was SENT (ms epoch). */
  at: number;
}

export function shouldSendCacheReport(input: {
  report: OfflineReadinessReport;
  last: CacheReportMemo | null;
  now: number;
  minIntervalMs?: number;
  keepaliveMs?: number;
}): boolean {
  const {
    report,
    last,
    now,
    minIntervalMs = CACHE_REPORT_MIN_INTERVAL_MS,
    keepaliveMs = CACHE_REPORT_KEEPALIVE_MS,
  } = input;

  // Cold start or reconnect: the server may know nothing about this device.
  if (!last) return true;

  // Keepalive, so a device that has been steady for hours still reads as live.
  if (now - last.at >= keepaliveMs) return true;

  // A state transition is always news, and it is the thing an operator is
  // waiting on. Suppressing "it just went ready" for five minutes would be
  // exactly the wrong trade. It cannot storm: a state change needs a new sync
  // payload, and sync polls every five minutes.
  if (report.state !== last.state) return true;

  return (
    cacheReportFingerprint(report) !== last.fingerprint &&
    now - last.at >= minIntervalMs
  );
}

export function cacheReportMemo(
  report: OfflineReadinessReport,
  now: number,
): CacheReportMemo {
  return {
    fingerprint: cacheReportFingerprint(report),
    state: report.state,
    at: now,
  };
}
